import { useCallback } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { toast } from '@cyphex/ui';
import type { ConversationId, FileId, MessageId, UserRef } from '@cyphex/types';
import { CHAT_KEYS } from '../constants';
import { newUuid } from '../lib/ids';
import { markdownToRich } from '../lib/markdown/blocks';
import { collectMentions } from '../lib/markdown/flatten';
import { optimisticMessage } from '../lib/optimistic';
import { appendMessage } from '../lib/timelineCache';
import { bumpConversation } from '../lib/conversationCache';
import { outboxStore } from '../store/outboxStore';
import type { OutboxItem } from '../types';
import { cachedMentionTargets } from './useMentionPicker';

export interface SendInput {
  text: string;
  fileIds?: FileId[];
  replyToId?: MessageId | null;
  threadRootId?: MessageId | null;
  scheduledFor?: string | null;
  voice?: { fileId: FileId; durationMs: number; waveform: number[] } | null;
}

/**
 * Enqueue-then-render. The message hits the timeline before any network call,
 * is durable across a reload, and reconciles by `clientMsgId` when the server
 * (or the socket) echoes it back.
 */
export function useSendMessage(
  conversationId: ConversationId | null,
  me: UserRef | null,
  lastSeq: number,
): (input: SendInput) => void {
  const client = useQueryClient();

  return useCallback(
    (input: SendInput) => {
      if (!conversationId || !me) return;
      const text = input.text.trim();
      if (!text && !input.voice && !(input.fileIds && input.fileIds.length > 0)) return;

      /* "@Maya Patel" becomes a mention of MAYA - with her id - here, against
         the people actually in this chat. Without the id the server discards
         the mention and she is told nothing. */
      const body = text
        ? markdownToRich(text, cachedMentionTargets(client, conversationId, me.id))
        : null;
      const item: OutboxItem = {
        clientMsgId: newUuid(),
        conversationId,
        createdAt: Date.now(),
        state: 'queued',
        attempts: 0,
        nextAttemptAt: 0,
        lastError: null,
        text,
        body,
        mentions: collectMentions(body),
        fileIds: input.fileIds ?? [],
        replyToId: input.replyToId ?? null,
        threadRootId: input.threadRootId ?? null,
        scheduledFor: input.scheduledFor ?? null,
        voice: input.voice ?? null,
        dlpRefusal: null,
        dlpAcknowledged: false,
      };

      void outboxStore.enqueue(item);

      if (item.scheduledFor) {
        void client.invalidateQueries({ queryKey: CHAT_KEYS.scheduled(conversationId) });
        toast.success('Message scheduled.');
        return;
      }

      const optimistic = optimisticMessage(item, me, lastSeq + 1);
      appendMessage(client, conversationId, optimistic, item.clientMsgId);
      bumpConversation(client, conversationId, optimistic, true);
    },
    [client, conversationId, lastSeq, me],
  );
}
